/**
 * The Critical Internal Function register (P429.1, Stream C).
 *
 * The second door: functions the bank performs for itself -- funding,
 * settlement, ledger close, screening -- which the five questions can
 * never admit. A function is raised over the services it rests on,
 * assessed through K1-K3 and F1-F3, with F4 contagion read from the
 * dependency graph and never entered, then challenged by the second
 * line and signed by the CRO and the CIO (Route 2). The Board notes the
 * register rather than approving each function.
 */
import { refusalMessage } from "shared/api/refusalMessage";
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSeatHolders } from "shared/api/useSeatHolders";
import { formatDate } from "shared/time/formatDate";

import { canReadCeremony, canRunOperations, canSignAs } from "shared/api/authority";
import { holdsSeat, useMe } from "shared/api/useMe";
import type {
  CifAssessmentIn,
  CifFunctionKind,
  CifRow,
  IBSAdmissionDecision,
} from "shared/api/types";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { isInternalTool } from "twin/ibs/internalTools";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { useToast } from "shared/toast/ToastProvider";
import {
  cifKeys,
  useAssessCif,
  useCifRegister,
  useCifServiceOptions,
  useCreateCif,
} from "twin/api/cif_hooks";
import {
  useChallengeAdmission,
  useSignoffAdmission,
} from "twin/api/ibs_admission_hooks";

const KINDS: CifFunctionKind[] = [
  "funding_settlement",
  "liquidity",
  "collateral",
  "positions",
  "ledger",
  "screening",
  "prudential_reporting",
  "other",
];

const QUALIFICATION = [
  "k1_firm_continuity",
  "k2_precedence",
  "k3_non_substitutable",
] as const;
const SCORED = [
  "time_to_impairment",
  "firm_severity",
  "regulatory_consequence",
] as const;

const BTN =
  "rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50";

function decisionTone(d: IBSAdmissionDecision | null): PillTone {
  if (!d) return "neutral";
  // An assessment awaiting its signatures is not yet a decision: green
  // beside "Candidate" and "0 admitted" said the opposite.
  if (d.workflow_state !== "approved") return "amber";
  if (d.inclusion_decision === "included") return "green";
  if (d.inclusion_decision === "excluded") return "red";
  return "amber";
}

export function CifRegisterView() {
  const { t } = useTranslation();
  const me = useMe();
  const register = useCifRegister();
  /* Round 16 N5 -- My Work and the function's page link here with
     `?cif=`, and the register opened nothing: the reader landed on a
     list and had to find the row. The named row opens. */
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get("cif"));
  const [raising, setRaising] = useState(false);

  if (me.data && !canReadCeremony(me.data)) {
    return (
      <p data-testid="cif-forbidden" className="text-sm text-slate-600">
        {t("modules.twin.cifs.forbidden")}
      </p>
    );
  }

  const data = register.data;
  const operator = canRunOperations(me.data);

  return (
    <section data-testid="cif-register-view" className="flex flex-col gap-4">
      <header>
        <p className="text-[11px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.cifs.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="ibs"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.twin.cifs.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("modules.twin.cifs.standfirst")}
        </p>
        {data ? (
          <p data-testid="cif-register-size" className="mt-2 text-sm text-slate-700">
            {t("modules.twin.cifs.size", {
              admitted: data.admitted_count,
              ceiling: data.register_size_max,
            })}
          </p>
        ) : null}
      </header>
      {/* Stitching review 5.5 -- what a reader meets on this register first. */}
      <FirstMeetings
        terms={["critical_internal_function", "route_2", "funding_edge", "contagion"]}
      />

      {operator ? (
        <div>
          <button
            type="button"
            className={BTN}
            data-testid="cif-raise-toggle"
            onClick={() => setRaising((v) => !v)}
          >
            {raising
              ? t("modules.twin.cifs.raise.close")
              : t("modules.twin.cifs.raise.open")}
          </button>
          {raising ? <RaiseForm onDone={() => setRaising(false)} /> : null}
        </div>
      ) : null}

      {register.isLoading ? (
        <p className="text-sm text-slate-500">{t("modules.twin.cifs.loading")}</p>
      ) : register.isError ? (
        <p data-testid="cif-error" className="text-sm text-rag-red">
          {t("modules.twin.cifs.loadFailed")}
        </p>
      ) : !data || data.rows.length === 0 ? (
        <p data-testid="cif-empty" className="text-sm text-slate-600">
          {t("modules.twin.cifs.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="cif-rows">
          {data.rows.map((row) => (
            <li
              key={row.id}
              data-testid="cif-row"
              data-id={row.id}
              className="rounded-xl border border-paper-2 bg-white p-4 shadow-card"
            >
              <CifSummary
                row={row}
                expanded={open === row.id}
                onToggle={() => setOpen(open === row.id ? null : row.id)}
              />
              {open === row.id ? <CifDetail row={row} /> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CifSummary({
  row,
  expanded,
  onToggle,
}: {
  row: CifRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const d = row.latest_decision;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <h2 className="font-display text-lg text-slate-900">{row.name}</h2>
        <p className="text-xs text-slate-500">
          {t(`modules.twin.cifs.kind.${row.function_kind}`)} ·{" "}
          {row.supporting_services.map((s) => s.name).join(", ") ||
            t("modules.twin.cifs.noServices")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill tone={row.admission_status === "admitted" ? "green" : "neutral"}>
          {t(`modules.twin.cifs.status.${row.admission_status}`, {
            defaultValue: row.admission_status,
          })}
        </StatusPill>
        <StatusPill tone={decisionTone(d)} testid="cif-decision">
          {!d
            ? t("modules.twin.cifs.decision.none")
            : d.workflow_state === "approved"
              ? t(`modules.twin.cifs.decision.${d.inclusion_decision}`)
              : /* Round 16 N2 -- one state per record: an unchallenged
                   assessment read "awaiting sign-off" over "Waiting on
                   the second-line challenge". */
                t(
                  d.workflow_state === "draft"
                    ? "modules.twin.cifs.decision.pendingChallenge"
                    : "modules.twin.cifs.decision.pending",
                  {
                    decision: t(
                      `modules.twin.cifs.decision.${d.inclusion_decision}`,
                    ).toLowerCase(),
                  },
                )}
        </StatusPill>
        {row.k2_flag ? (
          <StatusPill tone="amber" testid="cif-k2-flag">
            {t("modules.twin.cifs.k2Flag")}
          </StatusPill>
        ) : null}
        {/* Round 16 N5 -- no row opened the function's own page. */}
        <Link
          to={`/twin/ibs/${row.id}`}
          data-testid="cif-page"
          className="text-xs font-medium text-brand-brassDark hover:underline"
        >
          {/* Round 17 H39 -- four links all named "Function page"; each
              is named by its function. */}
          {t("modules.twin.cifs.pageOf", { name: row.name })}
        </Link>
        <button
          type="button"
          className={BTN}
          data-testid="cif-expand"
          aria-expanded={expanded}
          onClick={onToggle}
        >
          {expanded ? t("modules.twin.cifs.hide") : t("modules.twin.cifs.open")}
        </button>
      </div>
    </div>
  );
}

function CifDetail({ row }: { row: CifRow }) {
  const { t } = useTranslation();
  const me = useMe();
  const d = row.latest_decision;
  const c = row.contagion;
  return (
    <div className="mt-3 flex flex-col gap-3 border-t border-paper-2 pt-3 text-sm">
      <div data-testid="cif-contagion">
        <p className="font-medium text-slate-800">
          {t("modules.twin.cifs.contagion.heading", { band: c.band })}
        </p>
        <p className="text-slate-600">
          {(() => {
            const declared = c.basis === "declared_funding_and_regulatory_edges";
            return c.dependents.length > 0
              ? t(
                  declared
                    ? "modules.twin.cifs.contagion.dependentsDeclared"
                    : "modules.twin.cifs.contagion.dependents",
                  { names: c.dependents.join(", ") },
                )
              : t(
                  declared
                    ? "modules.twin.cifs.contagion.noneDeclared"
                    : "modules.twin.cifs.contagion.none",
                );
          })()}
        </p>
        <p className="text-xs text-slate-500">
          {c.basis === "declared_funding_and_regulatory_edges"
            ? t("modules.twin.cifs.contagion.declared")
            : t("modules.twin.cifs.contagion.lowerBound")}
        </p>
      </div>
      {row.k2_flag ? (
        <p data-testid="cif-k2-note" className="text-amber-800">
          {t("modules.twin.cifs.k2Note", {
            hours: c.tightest_dependent_tolerance_hours,
          })}
        </p>
      ) : null}
      {d ? (
        <div data-testid="cif-decision-detail">
          <p className="text-slate-700">
            {d.inclusion_decision === "excluded" && d.gate_failed_reason
              ? t("modules.twin.cifs.excludedBecause", { reason: d.gate_failed_reason })
              : d.triggers_fired.length > 0
                ? t(
                    /* Round 16 N2 -- "Admitted on" while not admitted. */
                    d.workflow_state === "approved"
                      ? "modules.twin.cifs.triggers"
                      : "modules.twin.cifs.triggersAssessed",
                    {
                      triggers: d.triggers_fired
                        .map((k) => t(`modules.twin.cifs.trigger.${k}`))
                        .join(", "),
                    },
                  )
                : t("modules.twin.cifs.noTrigger")}
          </p>
          {/* Round 17 N9 -- the answers that put it here, read-only, for
              the owner and every reader; the form is the operator's. */}
          <Answers decision={d} />
          <Governance row={row} decision={d} />
        </div>
      ) : null}
      {/* Round 17 G18 -- a record already in Route 2 (challenged, or
          signed by the CRO) is not offered a fresh assessment: recording
          one replaces the challenged assessment and restarts Route 2,
          and nothing said so. */}
      {canRunOperations(me.data) &&
      d &&
      ["challenged", "cro_signed", "cio_signed"].includes(d.workflow_state) ? (
        <p data-testid="cif-assess-in-route2" className="text-xs text-slate-600">
          {t("modules.twin.cifs.assess.inRoute2")}
        </p>
      ) : canRunOperations(me.data) && d?.workflow_state === "draft" ? (
        <ReassessFold row={row} />
      ) : canRunOperations(me.data) && d?.workflow_state !== "approved" ? (
        <AssessForm row={row} />
      ) : null}
    </div>
  );
}

/** Round 18 L13 -- an assessment recorded and waiting on its challenge
 *  was offered the whole form again beneath "Waiting on the second-line
 *  challenge", with no word that recording again replaces it (round 17
 *  G18's shape, one state earlier). Closed, and the warning said before
 *  it opens. */
function ReassessFold({ row }: { row: CifRow }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2" data-testid="cif-reassess">
      <p className="text-xs text-slate-600" data-testid="cif-reassess-warning">
        {t("modules.twin.cifs.assess.replaces")}
      </p>
      {open ? (
        <AssessForm row={row} />
      ) : (
        <button
          type="button"
          data-testid="cif-reassess-open"
          onClick={() => setOpen(true)}
          className="self-start text-xs font-medium text-brand-brassDark hover:underline"
        >
          {t("modules.twin.cifs.assess.recordAgain")}
        </button>
      )}
    </div>
  );
}

function Answers({ decision }: { decision: IBSAdmissionDecision }) {
  const { t } = useTranslation();
  const scores = (decision.scores ?? {}) as Record<string, number | undefined>;
  const why = (decision.rationale_by_criterion ?? {}) as Record<
    string,
    string | undefined
  >;
  const rows = [...QUALIFICATION, ...SCORED].filter((k) => scores[k] != null);
  if (rows.length === 0) return null;
  return (
    <dl
      data-testid="cif-answers"
      className="mt-2 grid gap-x-3 gap-y-0.5 text-xs sm:grid-cols-[auto_1fr]"
    >
      {rows.map((k) => {
        const v = scores[k];
        const qualifying = (QUALIFICATION as readonly string[]).includes(k);
        const value = qualifying
          ? v
            ? t("modules.twin.cifs.assess.yes")
            : t("modules.twin.cifs.assess.no")
          : t("modules.twin.cifs.answers.score", { value: v });
        return (
          <div key={k} className="contents">
            <dt className="text-slate-500">
              {t(`modules.twin.cifs.assess.criteria.${k}`)}
            </dt>
            <dd className="text-slate-800">
              {value}
              {why[k] ? ` -- ${why[k]}` : ""}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function Governance({
  row,
  decision,
}: {
  row: CifRow;
  decision: IBSAdmissionDecision;
}) {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const challenge = useChallengeAdmission();
  const signoff = useSignoffAdmission();
  const refresh = () => void qc.invalidateQueries({ queryKey: cifKeys.register() });
  const fail = (e: { detail?: string }) =>
    toast.push({
      tone: "error",
      message: t("modules.twin.cifs.actionFailed", { detail: refusalMessage(t, e) }),
    });
  const state = decision.workflow_state;
  const canChallenge = (me.data?.is_admin ?? false) || holdsSeat(me.data, "reviewer");
  const seats = useSeatHolders();

  if (state === "approved") {
    return (
      <p data-testid="cif-approved" className="mt-2 text-rag-green">
        {t("modules.twin.cifs.approved")}
      </p>
    );
  }
  if (state === "draft") {
    return (
      <div className="mt-2">
        <p className="text-slate-600">{t("modules.twin.cifs.awaitingChallenge")}</p>
        {canChallenge ? (
          <button
            type="button"
            className={`${BTN} mt-1`}
            data-testid="cif-challenge"
            disabled={challenge.isPending}
            onClick={() =>
              challenge.mutate(
                { ibsId: row.id, decisionId: decision.id },
                { onSuccess: refresh, onError: fail },
              )
            }
          >
            {t("modules.twin.cifs.challenge")}
          </button>
        ) : null}
      </div>
    );
  }
  const seat: "cro" | "cio" = state === "challenged" ? "cro" : "cio";
  /* Round 16 N2 -- "Challenged. Waiting on the CRO." named nobody, and
     the CRO's own panel did not say who had challenged or that the CIO
     signs next, though the record holds both. */
  const who = (role: "cro" | "cio") => {
    const holders = seats.data?.holders?.[role];
    return holders && holders.length === 1
      ? t("modules.twin.cifs.awaiting.named", {
          name: holders[0],
          role: role.toUpperCase(),
        })
      : t(`modules.twin.cifs.awaiting.seat.${role}`);
  };
  return (
    <div className="mt-2">
      <p className="text-slate-600" data-testid="cif-awaiting">
        {seat === "cro"
          ? t("modules.twin.cifs.awaiting.croNamed", {
              challenger:
                decision.challenge_reviewer_name ??
                t("modules.twin.cifs.awaiting.secondLine"),
              when: formatDate(decision.challenge_at, i18n.language),
              cro: who("cro"),
              cio: who("cio"),
            })
          : t("modules.twin.cifs.awaiting.cioNamed", {
              signer: decision.cro_signoff_name ?? who("cro"),
              when: formatDate(decision.cro_signoff_at ?? null, i18n.language),
              cio: who("cio"),
            })}
      </p>
      {canSignAs(me.data, seat) ? (
        <button
          type="button"
          className={`${BTN} mt-1`}
          data-testid={`cif-sign-${seat}`}
          disabled={signoff.isPending}
          onClick={() =>
            signoff.mutate(
              { ibsId: row.id, decisionId: decision.id, role: seat },
              {
                onSuccess: () => {
                  refresh();
                  toast.push({
                    tone: "success",
                    message: t("modules.twin.cifs.signed"),
                  });
                },
                onError: fail,
              },
            )
          }
        >
          {t(`modules.twin.cifs.sign.${seat}`)}
        </button>
      ) : null}
    </div>
  );
}

function AssessForm({ row }: { row: CifRow }) {
  const { t } = useTranslation();
  const toast = useToast();
  const assess = useAssessCif();
  const [values, setValues] = useState<Record<string, number>>({});
  const [why, setWhy] = useState<Record<string, string>>({});
  const keys = [...QUALIFICATION, ...SCORED];
  const complete = keys.every((k) => values[k] !== undefined && (why[k] ?? "").trim());

  const answer = (k: string) => ({
    value: values[k] ?? 0,
    rationale: (why[k] ?? "").trim(),
  });
  const submit = () => {
    const body: CifAssessmentIn = {
      qualification: {
        k1_firm_continuity: answer("k1_firm_continuity"),
        k2_precedence: answer("k2_precedence"),
        k3_non_substitutable: answer("k3_non_substitutable"),
      },
      scores: {
        time_to_impairment: answer("time_to_impairment"),
        firm_severity: answer("firm_severity"),
        regulatory_consequence: answer("regulatory_consequence"),
      },
    };
    assess.mutate(
      { cifId: row.id, body },
      {
        onSuccess: () =>
          toast.push({ tone: "success", message: t("modules.twin.cifs.assess.done") }),
        onError: (e) =>
          toast.push({
            tone: "error",
            message: t("modules.twin.cifs.actionFailed", {
              detail: refusalMessage(t, e),
            }),
          }),
      },
    );
  };

  return (
    <form
      data-testid="cif-assess-form"
      className="flex flex-col gap-2 rounded border border-paper-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <p className="font-medium text-slate-800">
        {t("modules.twin.cifs.assess.heading")}
      </p>
      <p className="text-xs text-slate-500">{t("modules.twin.cifs.assess.f4Note")}</p>
      {keys.map((k) => {
        const yesNo = (QUALIFICATION as readonly string[]).includes(k);
        const options = yesNo ? [1, 0] : [0, 1, 2, 3, 4];
        return (
          <div
            key={k}
            className="grid gap-1 sm:grid-cols-[14rem_6rem_1fr] sm:items-center"
          >
            <label htmlFor={`cif-${row.id}-${k}`} className="text-slate-700">
              {t(`modules.twin.cifs.assess.criteria.${k}`)}
            </label>
            <select
              id={`cif-${row.id}-${k}`}
              data-testid={`cif-assess-${k}`}
              className="rounded border border-slate-300 px-1 py-0.5"
              value={values[k] ?? ""}
              onChange={(e) =>
                setValues((v) => ({ ...v, [k]: Number(e.target.value) }))
              }
            >
              <option value="" disabled>
                {t("modules.twin.cifs.assess.choose")}
              </option>
              {options.map((o) => (
                <option key={o} value={o}>
                  {yesNo
                    ? o === 1
                      ? t("modules.twin.cifs.assess.yes")
                      : t("modules.twin.cifs.assess.no")
                    : String(o)}
                </option>
              ))}
            </select>
            <input
              aria-label={t("modules.twin.cifs.assess.why")}
              data-testid={`cif-assess-${k}-why`}
              className="rounded border border-slate-300 px-2 py-0.5"
              placeholder={t("modules.twin.cifs.assess.why")}
              value={why[k] ?? ""}
              onChange={(e) => setWhy((w) => ({ ...w, [k]: e.target.value }))}
            />
          </div>
        );
      })}
      <div>
        <button
          type="submit"
          className={BTN}
          data-testid="cif-assess-submit"
          disabled={!complete || assess.isPending}
        >
          {t("modules.twin.cifs.assess.submit")}
        </button>
      </div>
    </form>
  );
}

function RaiseForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const services = useCifServiceOptions(true);
  const create = useCreateCif();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CifFunctionKind>("funding_settlement");
  const [picked, setPicked] = useState<string[]>([]);
  const [rationale, setRationale] = useState("");
  const ready = name.trim() && picked.length > 0 && rationale.trim();
  const all = Array.isArray(services.data) ? services.data : [];
  /* Round 18 L13 -- the canteen booking, the wiki and the blog were
     offered as services a function might rest on. Behind the promote
     form's toggle, and a picked one stays listed. */
  const [showInternal, setShowInternal] = useState(false);
  const internal = all.filter((s) => isInternalTool(s));
  const options =
    showInternal || internal.length === 0
      ? all
      : all.filter((s) => !isInternalTool(s) || picked.includes(s.id));

  return (
    <form
      data-testid="cif-raise-form"
      className="mt-2 flex max-w-2xl flex-col gap-2 rounded border border-paper-2 bg-white p-3 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate(
          { name: name.trim(), function_kind: kind, service_ids: picked, rationale },
          {
            onSuccess: () => {
              toast.push({
                tone: "success",
                message: t("modules.twin.cifs.raise.done"),
              });
              onDone();
            },
            onError: (err) =>
              toast.push({
                tone: "error",
                message: t("modules.twin.cifs.actionFailed", {
                  detail: refusalMessage(t, err),
                }),
              }),
          },
        );
      }}
    >
      <label className="flex flex-col gap-1">
        {t("modules.twin.cifs.raise.name")}
        <input
          data-testid="cif-raise-name"
          className="rounded border border-slate-300 px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="flex flex-col gap-1">
        {t("modules.twin.cifs.raise.kind")}
        <select
          data-testid="cif-raise-kind"
          className="rounded border border-slate-300 px-2 py-1"
          value={kind}
          onChange={(e) => setKind(e.target.value as CifFunctionKind)}
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`modules.twin.cifs.kind.${k}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1">
        {t("modules.twin.cifs.raise.services")}
        <select
          multiple
          data-testid="cif-raise-services"
          className="h-32 rounded border border-slate-300 px-2 py-1"
          value={picked}
          onChange={(e) =>
            setPicked(Array.from(e.target.selectedOptions, (o) => o.value))
          }
        >
          {options.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {internal.length > 0 && (
          <button
            type="button"
            data-testid="cif-raise-internal-toggle"
            onClick={() => setShowInternal((v) => !v)}
            className="self-start text-[11px] text-brand-brassDark hover:underline"
          >
            {showInternal
              ? t("modules.twin.ibsAdmission.promote.hideInternal")
              : t("modules.twin.ibsAdmission.promote.showInternal", {
                  count: internal.length,
                })}
          </button>
        )}
      </label>
      <label className="flex flex-col gap-1">
        {t("modules.twin.cifs.raise.rationale")}
        <textarea
          data-testid="cif-raise-rationale"
          className="rounded border border-slate-300 px-2 py-1"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
        />
      </label>
      <div>
        <button
          type="submit"
          className={BTN}
          data-testid="cif-raise-submit"
          disabled={!ready || create.isPending}
        >
          {t("modules.twin.cifs.raise.submit")}
        </button>
      </div>
    </form>
  );
}
